/**
 * The customer accounting surface's only knowledge of the API Worker.
 *
 * Every path in spec §§3–9 that a CUSTOMER may call, and nothing else. No page,
 * hook or component builds a path: when plan 11 renames a report or plan 10
 * moves the import route, exactly one file changes and one test fails.
 *
 * Reads go through `productData()`, which unwraps `{ success, data }` and
 * throws `ProductDataError` on a failure envelope — so every function below
 * returns the payload directly and every caller handles failure by catching,
 * not by checking a flag.
 */
import { readCheckoutAttribution } from '@/features/analytics'
import { ProductDataError, productData, productDataRaw } from '@/lib/api/product-data-client'

// --- dashboard ----------------------------------------------------------

/** One row of `balanceDifference` (apps/api/src/bookkeeping/reports/types.ts). */
export interface BalanceAccountRow {
  ledgerAccountId: string
  code: string
  name: string
  ccy: string
  /** What the bank says, when we have it. `null` is "unknown", never zero. */
  statementBalanceMinor: number | null
  bookBalanceMinor: number
  /** statement − book. Exactly the unreviewed backlog. */
  differenceMinor: number | null
  unreviewedCount: number
  unansweredQuestions: number
}

// --- book setup ---------------------------------------------------------

export type ChartAccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense'

export type CatchUpOptionKey = 'this_year' | 'last_year'

export interface CatchUpOption {
  key: CatchUpOptionKey
  from: string
  to: string
  months: number
  amountCents: number
}

export interface CatchUpOffer {
  pricePerMonthCents: number
  options: CatchUpOption[]
  /** A Checkout the customer started and has not paid. */
  pending: { from: string; months: number; amountCents: number; createdAt: string } | null
  purchased: Array<{ from: string; to: string; months: number; paidAt: string }>
}

export interface BookSetupOffer {
  hasBook: boolean
  basis: 'cash'
  template: 'service'
  /** The first day the books cover: the 1st of the purchase month, or the formation date if later. */
  booksStart: string
  includedFrom: string
  chart: Array<{ code: string; name: string; type: ChartAccountType; subtype: string }>
  catchUp: CatchUpOffer
}

const NO_CATCH_UP: CatchUpOffer = { pricePerMonthCents: 15000, options: [], pending: null, purchased: [] }

/**
 * What the customer sees before their books exist, and the catch-up months on offer
 * (403 not entitled, 404 no company). An older API Worker may still send `startOptions`
 * (ignored) and no `catchUp` (no options).
 */
export async function fetchBookSetup(): Promise<BookSetupOffer> {
  const raw = await productData<
    Partial<BookSetupOffer> & { startOptions?: Array<{ key: string; date: string }>; defaultStart?: string }
  >('/api/customer/bookkeeping/setup')
  const legacyStart = raw.startOptions?.find((option) => option.key === raw.defaultStart)?.date ?? ''
  const booksStart = raw.booksStart ?? raw.includedFrom ?? legacyStart
  return {
    hasBook: raw.hasBook ?? false,
    basis: 'cash',
    template: 'service',
    booksStart,
    includedFrom: raw.includedFrom ?? booksStart,
    chart: raw.chart ?? [],
    catchUp: raw.catchUp ? { ...NO_CATCH_UP, ...raw.catchUp } : NO_CATCH_UP,
  }
}

/**
 * Starts a Stripe Checkout for catch-up months (409 `no_book`, 400 invalid option).
 * Carries the buyer's ad-click / GA ids and cookie consent for the server-side
 * purchase conversion; reading them never throws.
 */
export async function startCatchUpCheckout(option: CatchUpOptionKey): Promise<{ url: string }> {
  const attribution = readCheckoutAttribution()
  return productData<{ url: string }>('/api/customer/bookkeeping/catch-up/checkout', {
    method: 'POST',
    body: JSON.stringify({ option, ...(attribution ? { attribution } : {}) }),
  })
}

export interface CreatedBooks {
  bookId: string
  booksStartDate: string
  created: boolean
  connectionsAttached: number
  accountsBound: number
}

/** Opens the customer's books; safe to repeat (the API answers 200 when they already exist). */
export async function createBooks(): Promise<CreatedBooks> {
  return productData<CreatedBooks>('/api/customer/bookkeeping/books', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

// --- connections --------------------------------------------------------

export type ConnectionStatus = 'active' | 'login_required' | 'pending_disconnect' | 'revoked' | 'removed'
export type ConnectionSource = 'plaid' | 'stripe' | 'import'

export interface ConnectionSummary {
  id: string
  institutionName: string | null
  /** A `data:image/` URL, or null (older API Workers send neither field). */
  institutionLogo: string | null
  /** The bank's brand colour as the API reports it, or null. */
  institutionColor: string | null
  status: ConnectionStatus
  consentExpiresAt: string | null
  lastSyncedAt: string | null
  source: ConnectionSource
  /** Stamped by plan 10's NEW_ACCOUNTS_AVAILABLE webhook handler. */
  newAccountsAvailableAt: string | null
  /** A Stripe connection whose grant cannot read Stripe financial accounts; null otherwise or on an older API. */
  needsReconnect?: 'treasury_permission' | null
  accounts: Array<{
    name: string
    mask: string | null
    ccy: string
    status: string
  }>
}

export interface ConnectionsPayload {
  connections: ConnectionSummary[]
  hasBook: boolean
  /**
   * `active` inside the term; `grace`/`lapsed` once it ended (owner, 2026-10-14). An older API Worker
   * sends none, which reads as `active`: the entitlement checks decide as before.
   */
  access: 'active' | 'grace' | 'lapsed'
  /** When the 30-day grace ends (ISO), if known. */
  writableUntil: string | null
}

/**
 * `source` and `newAccountsAvailableAt` are defaulted, not required.
 *
 * The API Worker in production today answers the v1 `ConnectionSummary`, which
 * has neither field; plans 09 and 10 add them. This app Worker and the API
 * Worker deploy independently with no ordering (apps/app/AGENTS.md), so the
 * client tolerates the older shape instead of rendering "undefined".
 */
export async function fetchConnections(): Promise<ConnectionsPayload> {
  const payload = await productData<{
    connections: Array<
      Partial<Omit<ConnectionSummary, 'needsReconnect'>> &
        Pick<ConnectionSummary, 'id' | 'status'> & { needsReconnect?: unknown }
    >
    hasBook: boolean
    access?: unknown
    writableUntil?: unknown
  }>('/api/customer/feed/connections')
  return {
    hasBook: payload.hasBook === true,
    access: payload.access === 'lapsed' || payload.access === 'grace' ? payload.access : 'active',
    writableUntil: typeof payload.writableUntil === 'string' ? payload.writableUntil : null,
    connections: (payload.connections ?? []).map((row) => ({
      id: row.id,
      institutionName: row.institutionName ?? null,
      institutionLogo:
        typeof row.institutionLogo === 'string' && row.institutionLogo.startsWith('data:image/')
          ? row.institutionLogo
          : null,
      institutionColor: row.institutionColor ?? null,
      status: row.status,
      consentExpiresAt: row.consentExpiresAt ?? null,
      lastSyncedAt: row.lastSyncedAt ?? null,
      source: row.source ?? 'plaid',
      newAccountsAvailableAt: row.newAccountsAvailableAt ?? null,
      needsReconnect: row.needsReconnect === 'treasury_permission' ? 'treasury_permission' : null,
      accounts: row.accounts ?? [],
    })),
  }
}

export interface LinkTokenResult {
  linkToken: string
  expiration: string
}

export async function createLinkToken(): Promise<LinkTokenResult> {
  return productData<LinkTokenResult>('/api/customer/feed/link-token', {
    method: 'POST',
    body: '{}',
  })
}

export async function exchangePublicToken(publicToken: string): Promise<{ connectionId: string }> {
  return productData<{ connectionId: string }>('/api/customer/feed/exchange', {
    method: 'POST',
    body: JSON.stringify({ publicToken }),
  })
}

/** Update-mode token for a connection that needs the customer to sign in again. */
export async function createReconnectLinkToken(
  connectionId: string,
  options: { accountSelection?: boolean } = {},
): Promise<LinkTokenResult> {
  return productData<LinkTokenResult>(`/api/customer/feed/connections/${encodeURIComponent(connectionId)}/link-token`, {
    method: 'POST',
    body: JSON.stringify(options),
  })
}

export async function markConnectionRelinked(connectionId: string): Promise<{ status: 'active' }> {
  return productData<{ status: 'active' }>(
    `/api/customer/feed/connections/${encodeURIComponent(connectionId)}/relinked`,
    { method: 'POST', body: '{}' },
  )
}

// --- Stripe feed --------------------------------------------------------

/** One Stripe Treasury financial account in one currency, with its last read balance. */
export interface StripeTreasuryAccount {
  ccy: string
  ledgerAccountCode: string | null
  /** Minor units; `null` until the first balance read. */
  availableMinor: number | null
  pendingMinor: number | null
  asOf: string | null
}

/** One connected Stripe account, as its card on the Connections page shows it. */
export interface StripeConnectionStatus {
  /** `null` only from an API Worker that predates several Stripe accounts: nothing can address it. */
  id: string | null
  accountId: string | null
  /** The Stripe account's business name; `null` when Stripe did not tell us. */
  accountName: string | null
  /** Stripe's mark as a data URI (the one the account cards show); `null` from an older API. */
  logoUrl: string | null
  livemode: boolean | null
  authKind: 'oauth' | 'restricted_key'
  /** The Stripe App grant was revoked or expired: the customer must connect again. */
  needsReconnect: boolean
  /** The grant cannot read Stripe financial accounts (Treasury): reconnecting adds them. */
  needsTreasuryReconnect: boolean
  lastSyncedAt: string | null
  /** Currencies of the payments balance. Treasury accounts are listed apart. */
  currencies: string[]
  treasuryAccounts: StripeTreasuryAccount[]
}

export interface StripeFeedStatus {
  connected: boolean
  accountId: string | null
  livemode: boolean | null
  lastSyncedAt: string | null
  /** Currencies of the payments balance. Treasury accounts are listed apart. */
  currencies: string[]
  treasuryAccounts: StripeTreasuryAccount[]
  /** The Stripe App install is configured, so "Connect with Stripe" can be offered. */
  oauthAvailable: boolean
  /** How the live connection authenticates; `null` when nothing is connected. */
  authKind: 'oauth' | 'restricted_key' | null
  /** The Stripe App grant was revoked or expired: the customer must connect again. */
  needsReconnect: boolean
  /**
   * The grant cannot read Stripe financial accounts (Treasury): reconnecting
   * adds them. The API sends `needsReconnect: 'treasury_permission'` for it.
   */
  needsTreasuryReconnect?: boolean
  /**
   * Every connected Stripe account, oldest first. The flat fields above are
   * the oldest one's, kept while an API Worker can still be one release behind.
   */
  connections: StripeConnectionStatus[]
}

interface RawStripeBalance {
  ccy: string
  ledgerAccountCode: string | null
  availableMinor: number | null
  pendingMinor: number | null
  asOf: string | null
}

interface RawStripeConnection {
  id: string
  accountId: string | null
  accountName: string | null
  logoUrl?: string | null
  livemode: boolean | null
  authKind: 'oauth' | 'restricted_key'
  needsReconnect: boolean | 'treasury_permission'
  lastSyncedAt: string | null
  currencies: RawStripeBalance[]
  treasuryAccounts?: StripeTreasuryAccount[]
}

function stripeConnection(raw: Omit<RawStripeConnection, 'id'> & { id: string | null }): StripeConnectionStatus {
  return {
    id: raw.id,
    accountId: raw.accountId,
    accountName: raw.accountName,
    logoUrl: raw.logoUrl ?? null,
    livemode: raw.livemode,
    authKind: raw.authKind === 'oauth' ? 'oauth' : 'restricted_key',
    needsReconnect: raw.needsReconnect === true,
    needsTreasuryReconnect: raw.needsReconnect === 'treasury_permission',
    lastSyncedAt: raw.lastSyncedAt,
    currencies: raw.currencies.map((balance) => balance.ccy),
    treasuryAccounts: raw.treasuryAccounts ?? [],
  }
}

export async function fetchStripeFeed(): Promise<StripeFeedStatus> {
  const payload = await productData<
    Pick<StripeFeedStatus, 'connected' | 'accountId' | 'livemode' | 'lastSyncedAt'> &
      Partial<Pick<StripeFeedStatus, 'treasuryAccounts' | 'oauthAvailable' | 'authKind'>> & {
        needsReconnect?: boolean | 'treasury_permission'
        currencies: RawStripeBalance[]
        connections?: RawStripeConnection[]
      }
  >('/api/customer/feed/stripe')
  // An API Worker from before the Stripe App rollout omits the three new
  // fields: that means "no Connect with Stripe", i.e. today's key form.
  const { connections: rawConnections, ...flat } = payload
  // An API Worker from before several Stripe accounts sends the one connection
  // flat: it becomes the only card, with no id to disconnect it by.
  const connections = rawConnections
    ? rawConnections.map(stripeConnection)
    : payload.connected
      ? [
          stripeConnection({
            id: null,
            accountId: payload.accountId,
            accountName: null,
            livemode: payload.livemode,
            authKind: payload.authKind ?? 'restricted_key',
            needsReconnect: payload.needsReconnect ?? false,
            lastSyncedAt: payload.lastSyncedAt,
            currencies: payload.currencies,
            treasuryAccounts: payload.treasuryAccounts,
          }),
        ]
      : []
  return {
    ...flat,
    currencies: payload.currencies.map((balance) => balance.ccy),
    // An API Worker from before Stripe Treasury omits the list.
    treasuryAccounts: payload.treasuryAccounts ?? [],
    oauthAvailable: payload.oauthAvailable === true,
    authKind: payload.authKind ?? null,
    needsReconnect: payload.needsReconnect === true,
    needsTreasuryReconnect: payload.needsReconnect === 'treasury_permission',
    connections,
  }
}

/**
 * The restricted key travels in a POST body, once.
 *
 * Never a query string: a URL reaches logs, referrers and Sentry breadcrumbs,
 * and a Stripe key in any of those is a credential leak (root AGENTS.md).
 * `connectionId` names the connection whose key is replaced; without it the
 * key connects its Stripe account (a new one, or the one it already opens).
 */
export async function connectStripeFeed(
  apiKey: string,
  connectionId?: string,
): Promise<{ connectionId: string; currencies: string[] }> {
  return productData<{ connectionId: string; currencies: string[] }>('/api/customer/feed/stripe', {
    method: 'POST',
    body: JSON.stringify(connectionId ? { apiKey, connectionId } : { apiKey }),
  })
}

/**
 * Names one Stripe account (Stripe does not tell us its business name on a
 * Stripe App install). Its ledger account becomes `Stripe - <name>`.
 */
export async function renameStripeFeed(
  connectionId: string,
  name: string,
): Promise<{ connectionId: string; name: string }> {
  return productData<{ connectionId: string; name: string }>(
    `/api/customer/feed/stripe/connections/${encodeURIComponent(connectionId)}/name`,
    { method: 'POST', body: JSON.stringify({ name }) },
  )
}

/** Stops reading one Stripe account. Everything already in the books stays. */
export async function disconnectStripeFeed(
  connectionId: string,
): Promise<{ connectionId: string; disconnected: boolean }> {
  return productData<{ connectionId: string; disconnected: boolean }>(
    `/api/customer/feed/stripe/connections/${encodeURIComponent(connectionId)}/disconnect`,
    { method: 'POST', body: JSON.stringify({}) },
  )
}

/** Asks the API Worker for the Stripe install URL (it holds the signed `state`). */
export async function startStripeOAuth(): Promise<{ url: string }> {
  return productData<{ url: string }>('/api/customer/feed/stripe/oauth/start', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export interface StripeOAuthResult {
  connectionId: string
  accountId: string
  livemode: boolean
  currencies: string[]
  rotated: boolean
}

/**
 * Posts the one-time `code` and `state` Stripe handed the callback page.
 *
 * A Stripe code is single-use: the caller must send this exactly once and
 * never retry it.
 */
export async function completeStripeOAuth(code: string, state: string): Promise<StripeOAuthResult> {
  return productData<StripeOAuthResult>('/api/customer/feed/stripe/oauth/complete', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  })
}

// --- statement import ---------------------------------------------------

export type StatementFormat = 'csv' | 'ofx' | 'qfx'

export interface ImportBatchSummary {
  id: string
  fileName: string
  format: StatementFormat
  status: 'uploaded' | 'parsed' | 'mapped' | 'committed' | 'discarded'
  rowCount: number | null
  committedCount: number | null
  createdAt: string
}

export interface ImportsPayload {
  imports: ImportBatchSummary[]
}

export async function fetchImports(): Promise<ImportsPayload> {
  return productData<ImportsPayload>('/api/customer/feed/imports')
}

export interface StatementUploadResult {
  batchId: string
  fileName: string
  format: StatementFormat
}

/**
 * One multipart POST: the bytes are stored and the import batch is created in
 * the same call, so there is no presigned step and no document id to carry.
 *
 * `productDataRaw` rather than `productData`, so the upload owns its own
 * response handling: this is the only customer write whose route answers 201
 * with a body, and reading the raw `Response` keeps the envelope unwrap and
 * the failure message in one visible place instead of relying on the shared
 * client's narrow unwrap rule.
 *
 * Nothing sets a content-type: `productDataRaw` leaves a FormData body alone so
 * the browser keeps its own multipart boundary
 * (src/lib/api/product-data-client.ts). The route sniffs the format from the
 * file, so the client never asserts one — it only refuses obviously wrong
 * extensions before the request, as a courtesy.
 */
export async function uploadStatement(
  file: File,
  opts: { feedAccountId?: string } = {},
): Promise<StatementUploadResult> {
  const form = new FormData()
  form.append('file', file)
  if (opts.feedAccountId) form.append('feedAccountId', opts.feedAccountId)

  const response = await productDataRaw('/api/customer/feed/import/upload', {
    method: 'POST',
    body: form,
  })
  const text = await response.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }

  const envelope = body as {
    success?: boolean
    data?: StatementUploadResult
    error?: { code?: string; message?: string } | string
  } | null

  if (envelope?.success === false || !response.ok) {
    const message =
      typeof envelope?.error === 'string'
        ? envelope.error
        : (envelope?.error?.message ?? 'That statement could not be uploaded.')
    const serverCode = typeof envelope?.error === 'object' ? envelope.error?.code : undefined
    // Codes whose server wording tells the customer how to recover.
    const code =
      serverCode === 'not_entitled' ||
      serverCode === 'too_many_rows' ||
      serverCode === 'quota_exceeded' ||
      serverCode === 'empty_file'
        ? serverCode
        : 'UPLOAD_FAILED'
    throw new ProductDataError(response.status, code, message)
  }
  if (!envelope?.data) {
    throw new ProductDataError(response.status, 'UPLOAD_FAILED', 'That statement could not be uploaded.')
  }
  return envelope.data
}

// --- questions ----------------------------------------------------------

export interface QuestionOption {
  value: string
  label: string
  /** The gloss in brackets. For the customer, never for us. */
  hint?: string
  /** Whether choosing this option opens a free-text box. */
  freeText?: boolean
}

export interface RenderedQuestion {
  headline: string
  detail: string
  options: QuestionOption[]
}

export interface CustomerQuestion {
  id: string
  template: string
  /** Rendered at ASK time, so the answer always refers to what they were shown. */
  prompt: RenderedQuestion
  askedAt: string
}

export interface AnswerResult {
  accepted: true
  needsReview: boolean
  posted?: boolean
}

export async function fetchQuestions(): Promise<{
  questions: CustomerQuestion[]
}> {
  return productData<{ questions: CustomerQuestion[] }>('/api/customer/bookkeeping/questions')
}

export async function answerQuestion(
  questionId: string,
  answer: { value: string; text?: string },
): Promise<AnswerResult> {
  return productData<AnswerResult>(`/api/customer/bookkeeping/questions/${encodeURIComponent(questionId)}/answer`, {
    method: 'POST',
    body: JSON.stringify(answer.text === undefined ? { value: answer.value } : answer),
  })
}

// --- account transactions ----------------------------------------------

export interface LedgerTransaction {
  id: string
  categories: Array<{ code: string; name: string }>
  date: string
  description: string | null
  amountMinor: number
  ccy: string | null
  txnAmountMinor: number | null
  memo: string | null
}

export interface TransactionsPayload {
  openingBalanceMinor: number
  rows: LedgerTransaction[]
  nextCursor: string | null
}

export async function fetchAccountTransactions(
  accountId: string,
  cursor: string | null = null,
): Promise<TransactionsPayload> {
  const query = new URLSearchParams({ page: '1' })
  if (cursor !== null) query.set('cursor', cursor)
  const payload = await productData<TransactionsPayload>(
    `/api/customer/bookkeeping/accounts/${encodeURIComponent(accountId)}/transactions?${query}`,
  )
  // An older API ignores page/cursor. Never present its truncated history as a balance.
  if (!Array.isArray(payload.rows) || !Number.isSafeInteger(payload.openingBalanceMinor)) {
    throw new Error('Transaction history is being updated. Please try again shortly.')
  }
  return payload
}

// --- reports ------------------------------------------------------------

/** The three reports a customer may run (spec §8.2). */
export type ReportName = 'profit_and_loss' | 'balance_sheet' | 'account_transactions'
export type ReportPreset = 'this_quarter' | 'last_quarter' | 'this_year' | 'last_year'

export interface ReportRequest {
  report: ReportName
  preset?: ReportPreset
  from?: string
  to?: string
  compare?: 'prior_year' | null
  basis?: 'full' | 'cash'
  accountId?: string
}

export interface ReportColumn {
  key: string
  label: string
  kind: 'text' | 'money' | 'date' | 'link'
}

export interface ReportRow {
  cells: Record<string, string | number | null>
  accountId?: string
  entryId?: string
  depth?: number
  isTotal?: boolean
  isContra?: boolean
}

export interface ReportResult {
  report: ReportName
  title: string
  columns: ReportColumn[]
  rows: ReportRow[]
  totals?: Record<string, number>
  range: { from: string; to: string } | { asAt: string }
  /** True when any period in the range is still open. Render it. */
  provisional: boolean
  disclaimer: string
  caveats?: string[]
  /**
   * Reported by the engine; this surface has no currency switcher.
   *
   * Named `currency`, not `ccy`, because plan 11's `ReportResult` is the
   * canonical type and spells it that way. Every other shape in this file uses
   * `ccy` because that is what its own route answers — the two names are not
   * interchangeable, they belong to different contracts.
   */
  currency?: string
}

function reportQuery(request: ReportRequest): string {
  const params = new URLSearchParams()
  if (request.preset) params.set('preset', request.preset)
  if (request.from) params.set('from', request.from)
  if (request.to) params.set('to', request.to)
  if (request.compare) params.set('compare', request.compare)
  if (request.basis) params.set('basis', request.basis)
  if (request.accountId) params.set('accountId', request.accountId)
  return params.toString()
}

export async function fetchReport(request: ReportRequest): Promise<ReportResult> {
  const query = reportQuery(request)
  const payload = await productData<{ report: ReportResult }>(
    `/api/customer/bookkeeping/reports/${request.report}${query ? `?${query}` : ''}`,
  )
  return payload.report
}

/** The export path `downloadWithAuth` fetches. Same query, plus the format. */
export function reportExportPath(request: ReportRequest, format: 'csv' | 'pdf'): string {
  const query = reportQuery(request)
  return `/api/customer/bookkeeping/reports/${request.report}/export?${query ? `${query}&` : ''}format=${format}`
}

// --- year-end packages --------------------------------------------------

export interface YearEndPackageSummary {
  closeId: string
  fiscalYear: number
  status: 'started' | 'swept' | 'posted' | 'packaged' | 'closed' | 'reopened'
  closedAt: string | null
  fileName: string | null
}

export interface PackagesPayload {
  packages: YearEndPackageSummary[]
}

export async function fetchPackages(): Promise<PackagesPayload> {
  return productData<PackagesPayload>('/api/customer/bookkeeping/packages')
}

/** A short-lived presigned R2 URL. Never stored, never cached. */
export async function fetchPackageDownloadUrl(closeId: string): Promise<string> {
  const payload = await productData<{ url: string }>(
    `/api/customer/bookkeeping/packages/${encodeURIComponent(closeId)}/download`,
  )
  return payload.url
}
